import { Button, Form, Select } from "antd";
import { useForm } from "antd/lib/form/Form";
import FeatureMaterialIcon from "components/feature-icons/feature-material-icon";
import FeatureIconContainer from "components/feature-icons/feature-icon-container";
import { useMemo } from "react";
import { CloseOutlined } from "@ant-design/icons";
import {
  useCustomReports,
  useCustomReportsDispatch
} from "../context/custom-reports-provider";
import { ContextDatasetConfigType } from "../models";
import { updateGridSortConfig } from "../utils/utils";

const { Option } = Select as any;

export function DatasetSidebarHeader({
  title,
  onClose
}: {
  title: string;
  onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div
        className="font-bold text-sm p-4"
        data-testid="dataset-sidebar-header"
      >
        {title}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="border-none bg-transparent cursor-pointer p-2"
      >
        <CloseOutlined />
      </button>
    </div>
  );
}

export const entityOptions = [
  { label: "Materials", value: "materials", tag: "Materials" }
];
export const groupByOptions = [
  {
    label: "GC Representative",
    value: "gc_representative",
    tag: "Grouped by GC Representative"
  },
  {
    label: "Responsible Contractor",
    value: "responsible_contractor_name",
    tag: "Grouped by Responsible Contractor"
  },
  { label: "Assignee", value: "assignee_user", tag: "Grouped by Assignee" },
  {
    label: "Spec Section",
    value: "spec_sections",
    tag: "Grouped by Spec Section"
  },
  { label: "None", value: "none", tag: "No Grouping" }
];
export const linkedInfoViewOptions = [
  {
    label: "Show as inline table",
    value: "inline",
    tag: "Inline Links",
    helpText: "Shows all columns as a table inside the main table."
  },
  {
    label: "Show in condensed form",
    value: "condensed",
    tag: "Condensed Links",
    helpText: "Shows names of the Entity in Tags with more details on hover."
  },
  { label: "Do not show", value: "none", tag: "No Links" }
];

const linkedFeatureConfig: any = {
  1: {
    label: "Linked Submittal Information",
    viewOptions: [
      {
        label: "Show as inline table",
        value: "inline",
        tag: "Inline Links",
        helpText: "Shows all columns as a table inside the main table."
      },
      {
        label: "Show in condensed form",
        value: "condensed",
        tag: "Condensed Links",
        helpText:
          "Shows names of the Entity in Tags with more details on hover."
      },
      { label: "Do not show", value: "none", tag: "No Links" }
    ]
  },
  9: {
    label: "Linked Activity Information",
    viewOptions: [
      {
        label: "Show in condensed form",
        value: "condensed",
        tag: "Condensed Links",
        helpText:
          "Shows names of the Entity in Tags with more details on hover."
      },
      { label: "Do not show", value: "none", tag: "No Links" }
    ]
  }
};

export function DatasetSidebarContent({
  onUpdateDatasetConfig,
  onUpdateProjectReport
}: {
  onUpdateDatasetConfig: (datasetConfig: any) => void;
  onUpdateProjectReport: (projectReport: any) => void;
}) {
  const { datasetConfig, gridState, columnConfig } = useCustomReports();

  const dispatch: any = useCustomReportsDispatch();

  const [form] = useForm();

  const initialValues = useMemo(() => {
    const initValues: any = {
      primaryEntity: "materials",
      groupBy: datasetConfig?.groupByField,
      excludeWfComplete: datasetConfig?.additional_config?.exclude_wf_complete
    };
    datasetConfig?.linkedEntity.forEach((entityConfig) => {
      initValues[`linkedEntity_${entityConfig.feature_type_id}`] =
        entityConfig.display_type;
    });
    return initValues;
  }, [datasetConfig]);

  const onFinish = (values: any) => {
    const updatedSortConfig = updateGridSortConfig({
      sortState: gridState?.sortState || [],
      groupByField: values.groupBy,
      groupByOptions: groupByOptions.map((option) => option.value) || [],
      columnConfig: columnConfig || []
    });

    const contextPayload: ContextDatasetConfigType = {
      primaryEntity: values.primaryEntity,
      groupByField: values.groupBy,
      additional_config: {
        exclude_wf_complete: values.excludeWfComplete
      },
      linkedEntity: [
        {
          display_type: values.linkedEntity_1,
          feature_type_id: 1,
          only_governing_links: false
        },
        {
          display_type: values.linkedEntity_9,
          feature_type_id: 9,
          only_governing_links: false
        }
      ]
    };
    dispatch({ type: "UPDATE_DATASET_CONFIG", payload: contextPayload });
    dispatch({
      type: "UPDATE_GRID_STATE",
      payload: {
        sortState: updatedSortConfig
      }
    });

    onUpdateDatasetConfig(contextPayload);
    onUpdateProjectReport({ sorts_config: updatedSortConfig });
  };

  // const linkedEntityFieldWatch = Form.useWatch("linkedInfoView", form);

  return (
    <div className="px-4 pb-2 h-full" data-testid="dataset-sidebar">
      {datasetConfig && (
        <Form
          name="basic"
          autoComplete="off"
          layout="vertical"
          onFinish={onFinish}
          initialValues={initialValues}
          className="h-full"
          form={form}
          data-testid="dataset-config-form"
        >
          <div className="h-full flex flex-col justify-between">
            <div>
              <Form.Item
                label="Primary Entity"
                name="primaryEntity"
                data-testid="dataset-primary-entity"
              >
                <div className="flex items-start gap-2">
                  <div className="flex items-center justify-center">
                    <FeatureIconContainer
                      radiusClass="rounded-md"
                      childIconSize={12}
                      bgColorClass="bg-ciq-blue-250"
                    >
                      <FeatureMaterialIcon size={12} />
                    </FeatureIconContainer>
                  </div>
                  <div>Materials</div>
                </div>
                {/* <Select options={entityOptions} /> */}
              </Form.Item>

              <Form.Item
                label="Group By"
                name="groupBy"
                data-testid="dataset-group-by-field"
              >
                <Select
                  popupClassName="last-option-with-separator"
                  options={groupByOptions}
                  data-testid="dataset-group-by-select"
                />
              </Form.Item>

              {datasetConfig.linkedEntity.map((entityConfig) => {
                return (
                  <Form.Item
                    label={
                      linkedFeatureConfig[entityConfig.feature_type_id].label
                    }
                    name={`linkedEntity_${entityConfig.feature_type_id}`}
                    key={entityConfig.feature_type_id}
                    data-testid={`linked-entity-${entityConfig.feature_type_id}`}
                  >
                    <Select
                      optionLabelProp="label"
                      data-testid={`linked-entity-select-${entityConfig.feature_type_id}`}
                    >
                      {linkedFeatureConfig[
                        entityConfig.feature_type_id
                      ].viewOptions.map((option: any) => {
                        return (
                          <Option
                            key={option.value}
                            value={option.value}
                            label={option.label}
                          >
                            <div className="flex flex-col gap-1">
                              <div>{option.label}</div>
                              <div className="font-normal whitespace-normal text-black text-opacity-50 italic">
                                {option.helpText}
                              </div>
                            </div>
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                );
              })}

              {/* <Form.Item
                name="showOnlyGoverningLinks"
                valuePropName="checked"
                hidden={linkedEntityFieldWatch === "none"}
              >
                <Checkbox>Show Only Governing Links</Checkbox>
              </Form.Item> */}

              {/* <div className="font-medium text-xs text-ciq-gray-700 text-opacity-90 mt-5">
                Additional Options
              </div>
              <Form.Item name="excludeWfComplete" valuePropName="checked">
                <Checkbox>Exclude Delivered Materials</Checkbox>
              </Form.Item> */}
            </div>
            <div>
              <Form.Item className="w-full">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="w-full"
                  data-testid="dataset-run-button"
                >
                  Run
                </Button>
              </Form.Item>
            </div>
          </div>
        </Form>
      )}
    </div>
  );
}
